import { useEffect, useMemo, useState } from "react";
import { Dropdown } from "react-bootstrap";
import ActionDropdown from "../common/components/ActionDropdown";
import Icon from "../common/components/Icon";
import useNetwork from "../common/hooks/networkHook";
import { TextPromptDialog } from "../common/utils/dialogUtlities";
import useCredentials from "../common/hooks/credentialsHook";
import { useJobAssistance } from "../reducers/jobAssistanceSlice";
import { doJobAction, getJobActions, useJob } from "../reducers/jobSlice";
import PropTypes from 'prop-types';
import { useNetworkState } from "../common/hooks/appStateHook";
import { useSpecializedTranslation } from "../hooks/specializedTranslationHook";

/**
 * The job action button and associated infrastrcture for performing job actions
 * @component
 */
const JobActionButtonsImpl = ({ jobid }) => {
    const credentials = useCredentials();
    const network = useNetwork();
    const [jobActions, setJobActions] = useState([]);
    const [pendingAction, setPendingAction] = useState();
    const job = useJob(jobid);
    const { t } = useSpecializedTranslation(job);
    const assistance = useJobAssistance(jobid);

    const hasFull = useMemo(() => credentials.access.hasFull({ site: job.site, level: job.level, type: job.type }), [credentials, job])
    
    const hasFullAssistance = useMemo(() => {
        return credentials.access.hasFull(assistance.map(a => ({ type: a.type, level: a.level, site: a.site })));
    }, [credentials, assistance]);

    useEffect(() => {
        let superceeded = false;
        (async () => {
            if (jobid) {
                const actions = await getJobActions(network, jobid);
                if (!superceeded) {
                    setJobActions(actions);
                }
            } else {
                setJobActions([]);
            }
        })();
        return () => superceeded = true;
    }, [network, jobid, job?.type, job?.site, job?.level, assistance])

    const processAction = result => {
        if (result && pendingAction) {
            doJobAction(network, jobid, pendingAction.actionid, result);
        }
        setPendingAction();
    }

    const enableActions = hasFull || hasFullAssistance ;

    const actionClicked = a => {
        if (a.reason === true || a.reason === undefined) {
            setPendingAction(a);
        } else {
            doJobAction(network, jobid, a.actionid);
        }
    }

    return <>
        <TextPromptDialog showModal={!!pendingAction} title={t('actionReasonTitle', { action_name: pendingAction?.name })} message={t('actionReasonPrompt', { action_name: pendingAction?.name })} maxLength={512} placeholder={t('reason')} onClose={processAction}></TextPromptDialog>
        { jobActions?.length > 0 && enableActions ? <ActionDropdown label={t('actions')} icon={<Icon variant="solid" kind="fa-exchange-alt"></Icon>}>
            {jobActions.map(a => <Dropdown.Item key={'action_' + a.actionid} onClick={() => actionClicked(a)}>{a.name}</Dropdown.Item>)}
        </ActionDropdown> : null }
    </>
}

const JobActionButtons = ({ jobid }) => {
    const { isOnline } = useNetworkState();

    return ( isOnline && jobid && <JobActionButtonsImpl jobid={jobid}></JobActionButtonsImpl> )
}

JobActionButtons.propTypes = {
    jobid: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

export default JobActionButtons;
